import React from 'react';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { AppProvider, useApp } from './context/AppContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from './components/ui/sonner';
import Layout from './components/layout/Layout';
import AdirePattern from './components/shared/AdirePattern';

// Auth Pages
import LoginPage from './pages/auth/LoginPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import SetupAccountPage from './pages/auth/SetupAccountPage';
import RegisterPage from './pages/auth/RegisterPage';
import KycPage from './pages/auth/KycPage';
import VerifyEmailPage from './pages/auth/VerifyEmailPage';

// Pages
import LandingPage from './pages/LandingPage';
import SubscriberDashboard from './pages/subscriber/SubscriberDashboard';
import CareOfficerDashboard from './pages/care-officer/CareOfficerDashboard';
import SupervisorDashboard from './pages/supervisor/SupervisorDashboard';
import AdminDashboard from './pages/admin/AdminDashboard';
import DependantDashboard from './pages/dependant/DependantDashboard';
import VisitChecklistWizard from './pages/care-officer/VisitChecklistWizard';
import DependantsPage from './pages/subscriber/DependantsPage';
import AddDependentPage from './pages/subscriber/AddDependentPage';
import VisitsPage from './pages/subscriber/VisitsPage';
import AlertsPage from './pages/subscriber/AlertsPage';
import WalletPage from './pages/subscriber/WalletPage';
import VisitDetailPage from './pages/subscriber/VisitDetailPage';
import MessagesPage from './pages/subscriber/MessagesPage';
import SettingsPage from './pages/settings/SettingsPage';

// Supervisor Pages
import OfficersPage from './pages/supervisor/OfficersPage';
import PerformancePage from './pages/supervisor/PerformancePage';
import CreateCareOfficerPage from './pages/supervisor/CreateCareOfficerPage';
import CareOfficersListPage from './pages/supervisor/CareOfficersListPage';
import SchedulingPage from './pages/supervisor/SchedulingPage';
import SupervisorReviewsPage from './pages/supervisor/SupervisorReviewsPage';

// Admin Pages
import SubscribersPage from './pages/admin/SubscribersPage';
import AnalyticsPage from './pages/admin/AnalyticsPage';
import AdminOfficersPage from './pages/admin/AdminOfficersPage';
import PartnersPage from './pages/admin/PartnersPage';
import EmergencyAnalyticsPage from './pages/admin/EmergencyAnalyticsPage';
import AdminUsersPage from './pages/admin/AdminUsersPage';
import AdminSupervisorsPage from './pages/admin/AdminSupervisorsPage';
import CreateCustomerCareAgentPage from './pages/admin/CreateCustomerCareAgentPage';
import CustomerCareAgentsListPage from './pages/admin/CustomerCareAgentsListPage';

// Care Officer Pages
import AssignmentsPage from './pages/care-officer/AssignmentsPage';
import MarketplacePage from './pages/care-officer/MarketplacePage';
import CareOfficerSchedulePage from './pages/care-officer/CareOfficerSchedulePage';

import './App.css';

// Dashboard router based on role
const DashboardRouter = () => {
  const { role } = useApp();

  switch (role) {
    case 'subscriber':
      return <SubscriberDashboard />;
    case 'care_officer':
      return <CareOfficerDashboard />;
    case 'supervisor':
      return <SupervisorDashboard />;
    case 'customer_care':
      return <MessagesPage />;
    case 'admin':
      return <AdminDashboard />;
    case 'dependant':
      return <DependantDashboard />;
    default:
      return <SubscriberDashboard />;
  }
};

const ProtectedLayout = ({ children }) => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground">
        Loading...
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (user?.registered === false) {
    return <Navigate to="/kyc" replace />;
  }

  return <Layout>{children}</Layout>;
};

const authenticatedDestination = (user) => {
  if (user?.registered === false) return '/kyc';
  return user?.role === 'admin' ? '/admin/users' : '/dashboard';
};

const PublicAuthRoute = ({ children }) => {
  const { isAuthenticated, isLoading, user } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground">
        Loading...
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to={authenticatedDestination(user)} replace />;
  }

  return children;
};

const KycRoute = () => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground">
        Loading...
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (user?.registered === true) {
    return <Navigate to={authenticatedDestination(user)} replace />;
  }

  return <KycPage />;
};

const RoleGate = ({ children, roles }) => {
  const { user } = useAuth();
  if (!roles.includes(user?.role)) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};

// Role-aware Officers Page Router
const OfficersRouter = () => {
  const { role } = useApp();
  if (role === 'admin') {
    return <AdminOfficersPage />;
  }
  return <OfficersPage />;
};

const NotFoundPage = () => (
  <div className="relative min-h-screen overflow-hidden bg-[var(--color-cream)] px-6 py-20 text-center text-[var(--color-charcoal)]">
    <AdirePattern className="absolute inset-0 h-full w-full text-[var(--color-burgundy-700)]" opacity={0.06} />
    <div className="relative mx-auto max-w-xl">
      <p className="caption text-[var(--color-burgundy-700)]">Page not found</p>
      <h1 className="h1 mt-4">This page is not here<span className="text-[var(--color-burgundy-700)]">.</span></h1>
      <p className="body mt-4 text-[var(--color-ash-700)]">The care dashboard is still safe. Use the navigation to return to Magnolia.</p>
    </div>
  </div>
);

// Main App Routes
const AppRoutes = () => {
  const protectedElement = (element) => (
    <ProtectedLayout>{element}</ProtectedLayout>
  );
  const roleElement = (element, roles) => protectedElement(<RoleGate roles={roles}>{element}</RoleGate>);

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      
      {/* Auth Routes - Public */}
      <Route path="/login" element={<PublicAuthRoute><LoginPage /></PublicAuthRoute>} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
      <Route path="/setup-account/:token" element={<SetupAccountPage />} />
      <Route path="/register" element={<PublicAuthRoute><RegisterPage role="subscriber" /></PublicAuthRoute>} />
      <Route path="/register/supervisor" element={<PublicAuthRoute><RegisterPage role="supervisor" /></PublicAuthRoute>} />
      <Route path="/kyc" element={<KycRoute />} />
      <Route path="/verify-email/:token" element={<VerifyEmailPage />} />
      
      {/* Dashboard Routes - Protected (wrapped in Layout) */}
      <Route path="/dashboard" element={protectedElement(<DashboardRouter />)} />
      
      {/* Subscriber Routes */}
      <Route path="/dependants" element={protectedElement(<DependantsPage />)} />
      <Route path="/dependants/add" element={roleElement(<AddDependentPage />, ['subscriber'])} />
      <Route path="/dependants/:dependantId/edit" element={roleElement(<AddDependentPage />, ['subscriber'])} />
      <Route path="/visits" element={protectedElement(<VisitsPage />)} />
      <Route path="/visits/:visitId/checklist" element={roleElement(<VisitChecklistWizard />, ['care_officer'])} />
      <Route path="/visits/:visitId" element={protectedElement(<VisitDetailPage />)} />
      <Route path="/alerts" element={protectedElement(<AlertsPage />)} />
      <Route path="/messages" element={protectedElement(<MessagesPage />)} />
      <Route path="/wallet" element={protectedElement(<WalletPage />)} />
      
      {/* Care Officer Routes */}
      <Route path="/schedule" element={roleElement(<CareOfficerSchedulePage />, ['care_officer'])} />
      <Route path="/checklist" element={protectedElement(<Navigate to="/schedule" replace />)} />
      <Route path="/assignments" element={protectedElement(<AssignmentsPage />)} />
      <Route path="/marketplace" element={protectedElement(<MarketplacePage />)} />
      <Route path="/wallet-requests" element={protectedElement(<WalletPage />)} />
      
      {/* Supervisor Routes */}
      <Route path="/reviews" element={roleElement(<SupervisorReviewsPage />, ['supervisor'])} />
      <Route path="/scheduling" element={roleElement(<SchedulingPage />, ['supervisor'])} />
      <Route path="/care-officers" element={roleElement(<CareOfficersListPage />, ['supervisor'])} />
      <Route path="/care-officers/create" element={roleElement(<CreateCareOfficerPage />, ['supervisor'])} />
      <Route path="/officers" element={protectedElement(<OfficersRouter />)} />
      <Route path="/coverage" element={protectedElement(<SupervisorDashboard />)} />
      <Route path="/performance" element={protectedElement(<PerformancePage />)} />
      <Route path="/anomaly-flags" element={protectedElement(<Navigate to="/reviews" replace />)} />
      
      {/* Admin Routes */}
      <Route path="/admin/users" element={protectedElement(<AdminUsersPage />)} />
      <Route path="/admin/customer-care-agents" element={roleElement(<CustomerCareAgentsListPage />, ['admin'])} />
      <Route path="/admin/customer-care-agents/create" element={roleElement(<CreateCustomerCareAgentPage />, ['admin'])} />
      <Route path="/analytics" element={protectedElement(<AnalyticsPage />)} />
      <Route path="/subscribers" element={protectedElement(<SubscribersPage />)} />
      <Route path="/partners" element={protectedElement(<PartnersPage />)} />
      <Route path="/emergency-analytics" element={protectedElement(<EmergencyAnalyticsPage />)} />
      <Route path="/supervisors" element={roleElement(<AdminSupervisorsPage />, ['admin'])} />
      <Route path="/billing" element={protectedElement(<AdminDashboard />)} />
      
      {/* Settings - all roles */}
      <Route path="/settings" element={protectedElement(<SettingsPage />)} />
      
      {/* 404 */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
};

function App() {
  return (
    <AuthProvider>
      <AppProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster position="top-right" />
        </BrowserRouter>
      </AppProvider>
    </AuthProvider>
  );
}

export default App;
